import { Box, VStack } from '@web/components/atoms/Layout'
import { Image } from '@web/components/atoms/Image'
import { useVirtualGiftSection } from './VirtualGiftSectionProvider'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { v4 } from 'uuid'

type VirtualGiftSectionPreviewImageItemProps = {
  id?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  itemOptions: any
  index: number
  onFinalise?: () => Promise<boolean>
}

const MIN_F = 12
const MAX_F = 60

const VirtualGiftSectionImageOverlayText = ({
  itemOptions,
  inputValue = '',
  index,
  onFitSize,
  forcedFontSize,
}: {
  inputValue: string
  itemOptions: any
  index: number
  onFitSize?: (index: number, size: number) => void
  forcedFontSize?: number
}) => {
  const context = useVirtualGiftSection()
  const region = (itemOptions?.inputs?.[index] || itemOptions?.inputs?.[0])?.region
  const maxCharacters = itemOptions?.inputs?.[index]?.maxCharacters || 50
  const truncatedValue = inputValue.length > maxCharacters ? inputValue.slice(0, maxCharacters) : inputValue
  const censorInput = context.censorInput
  const [overlayText, setOverlayText] = useState(censorInput ? '' : truncatedValue)

  const boxRef = useRef<HTMLDivElement>(null)
  const forcedFontSizeRef = useRef(forcedFontSize)

  useEffect(() => {
    forcedFontSizeRef.current = forcedFontSize
  }, [forcedFontSize])

  function fit() {
    const box = boxRef.current
    if (!box) return

    // Always reset to max first so we measure from a clean state
    box.style.fontSize = MAX_F + 'px'

    const availH = box.clientHeight
    let size = MAX_F

    while (size > MIN_F && box.scrollHeight > availH) {
      size--
      box.style.fontSize = size + 'px'
    }

    console.log('hmm')
    onFitSize?.(index, size)

    // Re-apply forced size if parent has already determined the minimum
    if (forcedFontSizeRef.current !== undefined) {
      box.style.fontSize = forcedFontSizeRef.current + 'px'
    }
  }

  useEffect(() => {
    if (!censorInput) {
      setOverlayText(truncatedValue)
      return
    }

    if (!truncatedValue) {
      setOverlayText('')
      return
    }

    const result = censorInput(truncatedValue)
    if (typeof result === 'string') {
      setOverlayText(result)
      return
    }

    let cancelled = false
    result
      .then((text) => {
        if (!cancelled) {
          setOverlayText(text)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setOverlayText(truncatedValue)
        }
      })

    return () => {
      cancelled = true
    }
  }, [truncatedValue, censorInput])

  useEffect(() => {
    fit()
  }, [overlayText])

  useEffect(() => {
    if (!truncatedValue) {
      return
    }

    // on initial render with a value we wait for the box to render before we try and fit the text
    setTimeout(() => {
      fit()
    }, 1)
  }, [])

  // Apply the forced (minimum) font size from parent once all siblings have measured
  useEffect(() => {
    const box = boxRef.current
    if (!box || forcedFontSize === undefined) return
    box.style.fontSize = forcedFontSize + 'px'
  }, [forcedFontSize])

  return (
    <div
      ref={boxRef}
      style={{
        position: 'absolute',
        left: region?.x + '%',
        top: region?.y + '%',
        width: region?.w + '%',
        height: region?.h + '%',
        boxSizing: 'border-box',
        overflow: 'hidden',
        fontSize: MAX_F,
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        lineHeight: 1.3,
        outline: 'none',
        textAlign: 'center',
        color: 'white',
        fontWeight: 'bold',
        fontFamily: 'Inter, sans-serif',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {overlayText}
    </div>
  )
}

export const VirtualGiftSectionPreviewImageItem = ({
  id,
  itemOptions,
  index,
}: VirtualGiftSectionPreviewImageItemProps) => {
  const context = useVirtualGiftSection()
  const currentItem = context.items[index]

  const [fittedSizes, setFittedSizes] = useState<number[]>([])

  const handleFitSize = useCallback((i: number, size: number) => {
    setFittedSizes((prev) => {
      const next = [...prev]
      next[i] = size
      return next
    })
  }, [])

  const minFontSize = Math.min(...fittedSizes.filter((s) => s !== undefined))

  return (
    <Box className="ge-virtual-gift-preview">
      <Box className="ge-virtual-gift-preview-image" position="relative">
        <Image src={itemOptions?.imageUrl} />
        {currentItem.value.map((inputValue, index) => (
          <VirtualGiftSectionImageOverlayText
            key={`overlay-text-${index}_${id ?? ''}`}
            itemOptions={itemOptions}
            inputValue={inputValue}
            index={index}
            onFitSize={handleFitSize}
            forcedFontSize={minFontSize > 0 ? minFontSize : undefined}
          />
        ))}
      </Box>
    </Box>
  )
}

const VirtualGiftSectionPreviewImage = () => {
  const { items, settings } = useVirtualGiftSection()
  const id = useMemo(() => v4(), [])

  return (
    <VStack alignItems="center" gap="24px" pointerEvents="none">
      {items.map((_, index) => {
        const itemOptions = settings.styles?.options?.items?.[index] || settings.styles?.options?.items?.[0]
        return (
          <VirtualGiftSectionPreviewImageItem id={id} key={`${id}-${index}`} itemOptions={itemOptions} index={index} />
        )
      })}
    </VStack>
  )
}

export default VirtualGiftSectionPreviewImage
